import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { tr, enUS, de, es, fr } from 'date-fns/locale';

// Use window.location.origin so nginx proxy handles backend routing
const BACKEND_URL = window.location.origin;

const dateFnsLocales = {
  tr,
  en: enUS,
  de,
  es,
  fr
};

export const PublicChangelog = () => {
  const { tenantSlug } = useParams();
  const { t, language } = useLanguage();
  const [changelogs, setChangelogs] = useState([]);
  const [projectName, setProjectName] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantSlug) {
      fetchChangelogs();
      fetchProjectName();
    }
  }, [tenantSlug]);

  const fetchProjectName = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tenants/lookup/${tenantSlug}`);
      setProjectName(response.data.name);
    } catch (error) {
      console.error('Failed to fetch project name:', error);
    }
  };

  const fetchChangelogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/changelog/${tenantSlug}`);
      setChangelogs(response.data);
    } catch (error) {
      console.error('Failed to fetch changelog:', error);
      toast.error(t.failedToLoadChangelog);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white">{t.loadingChangelog}</div>
      </div>
    );
  }

  const dateFnsLocale = dateFnsLocales[language] || enUS;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{projectName || tenantSlug || 'Project'} - Changelog | FeedPanels</title>
        <meta name="description" content={`Stay updated with the latest changes and updates for ${projectName || 'this project'}. View version history and release notes.`} />
        <link rel="canonical" href={`https://feedpanels.com/changelog/${tenantSlug}`} />
      </Helmet>
      <PublicHeader
        title={t.changelog}
        description={t.stayUpdated}
        projectName={projectName}
        tenantSlug={tenantSlug}
      />

      <div className="max-w-4xl mx-auto px-3 md:px-6 py-6 md:py-8">
        {changelogs.length === 0 ? (
          <Card className="p-8 md:p-12 text-center">
            <div className="w-12 md:w-16 h-12 md:h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
              <Sparkles className="w-6 md:w-8 h-6 md:h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mb-1 md:mb-2">{t.noReleasesYet}</p>
            <p className="text-xs md:text-sm text-gray-400 dark:text-gray-500">{t.checkBackSoon}</p>
          </Card>
        ) : (
          <div className="space-y-4 md:space-y-8">
            {changelogs.map((entry) => (
              <Card key={entry.id} data-testid={`changelog-${entry.id}`}>
                <CardContent className="p-4 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-0 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 md:space-x-3 mb-2 flex-wrap gap-2 md:gap-0">
                        <Badge className="bg-cyan-600 text-white px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm">
                          {entry.version}
                        </Badge>
                        <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white break-words">{entry.title}</h2>
                      </div>
                      <div className="flex items-center text-xs md:text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3 md:w-4 h-3 md:h-4 mr-1 flex-shrink-0" />
                        {format(new Date(entry.release_date), 'MMMM d, yyyy', { locale: dateFnsLocale })}
                      </div>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-xs md:text-sm text-gray-700 dark:text-gray-300 font-sans leading-relaxed overflow-x-auto">
                      {entry.content}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};